import { TimeSlotName } from '../services/treatmentAdministration';

export interface FrequencySlotConfig {
  names: TimeSlotName[];
  labels: string[]; // translation keys for abstract slots, literal strings for hour-based
  isHourBased: boolean;
}

/**
 * Maps frequency values to their time slot configurations.
 * Existing frequencies use abstract slot names (morning, noon, etc.).
 * New frequencies (4h, 2h, 48h, 72h) use hour-based slot names (h06, h10, etc.).
 */
const FREQUENCY_SLOT_MAP: Record<string, { names: TimeSlotName[]; labelKeys: string[]; isHourBased: boolean }> = {
  '24h': {
    names: ['morning'],
    labelKeys: ['hospital.treatment-inpatient-time-slot-morning'],
    isHourBased: false,
  },
  '12h': {
    names: ['morning', 'evening'],
    labelKeys: [
      'hospital.treatment-inpatient-time-slot-morning',
      'hospital.treatment-inpatient-time-slot-evening',
    ],
    isHourBased: false,
  },
  '8h': {
    names: ['morning', 'noon', 'evening'],
    labelKeys: [
      'hospital.treatment-inpatient-time-slot-morning',
      'hospital.treatment-inpatient-time-slot-noon',
      'hospital.treatment-inpatient-time-slot-evening',
    ],
    isHourBased: false,
  },
  '6h': {
    names: ['morning', 'noon', 'evening', 'bedtime'],
    labelKeys: [
      'hospital.treatment-inpatient-time-slot-morning',
      'hospital.treatment-inpatient-time-slot-noon',
      'hospital.treatment-inpatient-time-slot-evening',
      'hospital.treatment-inpatient-time-slot-bedtime',
    ],
    isHourBased: false,
  },
  'nocte': {
    names: ['bedtime'],
    labelKeys: ['hospital.treatment-inpatient-time-slot-bedtime'],
    isHourBased: false,
  },
  'stat': {
    names: ['morning', 'noon', 'evening', 'bedtime'],
    labelKeys: [
      'hospital.treatment-inpatient-time-slot-morning',
      'hospital.treatment-inpatient-time-slot-noon',
      'hospital.treatment-inpatient-time-slot-evening',
      'hospital.treatment-inpatient-time-slot-bedtime',
    ],
    isHourBased: false,
  },
  'prn': {
    names: ['morning', 'noon', 'evening', 'bedtime'],
    labelKeys: [
      'hospital.treatment-inpatient-time-slot-morning',
      'hospital.treatment-inpatient-time-slot-noon',
      'hospital.treatment-inpatient-time-slot-evening',
      'hospital.treatment-inpatient-time-slot-bedtime',
    ],
    isHourBased: false,
  },
  '4h': {
    names: ['h06', 'h10', 'h14', 'h18', 'h22', 'h02'],
    labelKeys: ['06:00', '10:00', '14:00', '18:00', '22:00', '02:00'],
    isHourBased: true,
  },
  '2h': {
    names: ['h06', 'h08', 'h10', 'h12', 'h14', 'h16', 'h18', 'h20', 'h22', 'h00', 'h02', 'h04'],
    labelKeys: ['06:00', '08:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00', '22:00', '00:00', '02:00', '04:00'],
    isHourBased: true,
  },
  '48h': {
    names: ['h08'],
    labelKeys: ['08:00'],
    isHourBased: true,
  },
  '72h': {
    names: ['h08'],
    labelKeys: ['08:00'],
    isHourBased: true,
  },
};

// Default config for unknown frequencies
const DEFAULT_CONFIG = FREQUENCY_SLOT_MAP['6h'];

/**
 * Returns time slot names and display labels for a given frequency.
 * @param frequency - The frequency value (e.g., '4h', '12h', '48h')
 * @param translate - Translation function for abstract slot labels
 */
export function getTimeSlotsForFrequency(
  frequency: string,
  translate: (key: string) => string
): FrequencySlotConfig {
  const config = FREQUENCY_SLOT_MAP[frequency] || DEFAULT_CONFIG;

  const labels = config.isHourBased
    ? config.labelKeys // Hour labels are already display-ready
    : config.labelKeys.map((key) => translate(key));

  return {
    names: config.names,
    labels,
    isHourBased: config.isHourBased,
  };
}

/**
 * Checks if a given day is an active administration day for multi-day frequencies.
 * Q48H: active on even-numbered days from treatment start.
 * Q72H: active every 3rd day from treatment start.
 * All other frequencies: always active.
 *
 * @param frequency - The frequency value
 * @param day - The calendar day to check
 * @param treatmentStartDate - The treatment's start date (reference point)
 */
export function isDayActiveForFrequency(
  frequency: string,
  day: Date,
  treatmentStartDate?: Date | string | null
): boolean {
  if (frequency !== '48h' && frequency !== '72h') {
    return true;
  }

  if (!treatmentStartDate) {
    return true; // Graceful fallback: all days active if no start date
  }

  const start = typeof treatmentStartDate === 'string'
    ? new Date(treatmentStartDate + 'T00:00:00')
    : treatmentStartDate;

  // Normalize both dates to midnight UTC to avoid timezone issues
  const startDay = new Date(Date.UTC(start.getFullYear(), start.getMonth(), start.getDate()));
  const checkDay = new Date(Date.UTC(day.getFullYear(), day.getMonth(), day.getDate()));

  const diffMs = checkDay.getTime() - startDay.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  // Before treatment start date: never active
  if (diffDays < 0) {
    return false;
  }

  if (frequency === '48h') {
    return diffDays % 2 === 0;
  }

  if (frequency === '72h') {
    return diffDays % 3 === 0;
  }

  return true;
}

/**
 * Returns true if this frequency uses hour-based time slot identifiers.
 */
export function isHourBasedFrequency(frequency: string): boolean {
  const config = FREQUENCY_SLOT_MAP[frequency];
  return config ? config.isHourBased : false;
}

/**
 * Returns the valid time slot names for a given frequency.
 * Used for validation on both client and can be mirrored on server.
 */
export function getValidTimeSlotsForFrequency(frequency: string): TimeSlotName[] {
  const config = FREQUENCY_SLOT_MAP[frequency] || DEFAULT_CONFIG;
  return config.names;
}
